import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { toast } from "sonner";
import "./barCodeScanner.css";
import { Barcode, Camera, X } from "lucide-react";

import { uiAlert } from "../../../../components/alerts/customAlert/uiAlert";

const BarcodeScanner = ({ setScanCodeString, handleProductScan, setOpenScannerModal, isCreatingProduct = false }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState("");
  const [useHandScanner, setUseHandScanner] = useState(false);
  const [useSdkScanner, setUseSdkScanner] = useState(false);

  const scannerRef = useRef(null);
  const beepRef = useRef(null);
  const readerElementId = "barcode-scanner-reader";

  //sdk setup

  /*   let licenseKey =
    "QjxYkqmtSUl2NOnQxI8+wHJQ3Mw9bR" +
    "v/XNsIXC/QGpm1lJ0JKNVC6u3or+lg" +
    "LixvA4MXd3inQlwHmA4RHZXAGiOPn7" +
    "9yqhICY0h56A7X6o19wD3mM70+zQbr" +
    "0+j8RwPVkYh5SC6y7lHbGmhPvh5Jn+" +
    "q2WbXeycp/s2IISoLvsC5P12ve93n3" +
    "noat5FnnZbAIW+wOBglsVZtrV4zdA8" +
    "+iPzyOtBT47VQXRrIku7NqeQfNafTX" +
    "xn/TnTNz/VzY96QQuOmgqSAhdvtM5a" +
    "6s3HrN0jxOdg11MpONUMZ+zg5pTiSy" +
    "qAgW+NhBxxcnEEiPx/jWwjEGZRp7IU" +
    "oGFdVdtXnh4A==\nU2NhbmJvdFNESw" +
    "psb2NhbGhvc3R8c3RvcmVsZW5zZS5j" +
    "b20KMTc2NTg0MzE5OQo4Mzg4NjA3Cj" +
    "g=\n";

  const { sdk, ready } = useScanbot({ licenseKey });
  console.log("sdk ready?", ready);

  const openScanner = async () => {
    if (!sdk) {
      uiAlert("Scanner not available now. Please try again in a moment.", "error");
      return;
    }
    // create a default configuration
    const config = new sdk.UI.Config.BarcodeScannerScreenConfiguration();
    config.actionBar.flashButton.visible = true;
    config.userGuidance.title.text = "Align barcodes within the frame to scan";
    // tweak config if you want: config.scannerConfiguration.someField = ...
    // configure for multiple scans
    const useCase = new sdk.UI.Config.MultipleScanningMode();
    useCase.arOverlay.automaticSelectionEnabled = true;

    useCase.sheet.mode = "COLLAPSED_SHEET";

    // Set the height for the collapsed sheet.
    useCase.sheet.collapsedVisibleHeight = "LARGE";

    // Configure the submit button on the sheet.
    useCase.sheetContent.submitButton.text = "Submit";
    useCase.sheetContent.submitButton.foreground.color = "#000000";
    // assign back to config
    config.useCase = useCase;

    // then present RTU UI
    // const result = await sdk.UI.BarcodeScanner.present(config);
    // `result` contains scanned barcode(s) depending on config
    const result = await sdk.UI.createBarcodeScanner(config);
    console.log("scan result", result.items[0].count, result.items[0].barcode.text);

    // confirm the license and implement your logic
  }; */

  useEffect(() => {
    beepRef.current = new Audio("/sounds/beep.mp3");
    return () => {
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    try {
      setError("");
      const html5QrCode = new Html5Qrcode(readerElementId, {
        formatsToSupport: [
          Html5QrcodeSupportedFormats.QR_CODE,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.CODE_93,
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.ITF,
          Html5QrcodeSupportedFormats.CODABAR,
        ],
        verbose: false,
      });
      scannerRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 350, height: 200 },
        },
        (decodedText, decodedResult) => {
          onDetected(decodedText, decodedResult);
        },
        undefined
      );

      setIsScanning(true);
    } catch (err) {
      setError("Failed to start camera. Please ensure camera permissions are granted.");
      console.error(err);
      toast.error("Error starting barcode scanner: " + err.message);
    }
  };

  const onDetected = (decodedText, decodedResult) => {
    // Prevent handling the same frame / repeated callbacks

    // Play beep sound
    if (beepRef.current) {
      beepRef.current.currentTime = 0;
      beepRef.current.play().catch(() => {});
    }
    const newResult = {
      id: Date.now().toString(),
      text: decodedText,
      format: decodedResult.result.format?.formatName || "Unknown",
      timestamp: new Date(),
    };
    //vibrate device if supported
    if (navigator.vibrate) {
      navigator.vibrate(200);
    }

    // now use the decodedText as needed
    toast.success(`Scanned: ${decodedText}`);
    setScanCodeString(decodedText);
    handleProductScan(decodedText); // Trigger product scan handling
    setResults((prev) => [newResult, ...prev]);
    isCreatingProduct ? handleCloseScanner() : stopScanning();

    // startScanning again after a short delay
  };

  const stopScanning = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        setIsScanning(false);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleCloseScanner = () => {
    // stopScanning();
    setOpenScannerModal(false);
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(""), 2000);
  };

  const clearResults = () => {
    setResults([]);
  };

  return (
    <div
      onClick={() => {
        setOpenScannerModal(false);
        // stopScanning();
      }}
      className='barcodeDropOverlay'
    >
      <div onClick={(e) => e.stopPropagation()} className='barcode-scanner-container'>
        <div className='barcode-scanner-header'>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", justifyContent: "space-between" }}>
            <h1 className='barcode-scanner-title'>Barcode Scanner</h1>
            <span className='barcode-scanner-close' onClick={handleCloseScanner}>
              <X />
            </span>
          </div>
          <p className='barcode-scanner-subtitle'>Scan any barcode </p>
        </div>

        {useHandScanner ? (
          <HandheldScanner
            setUseHandScanner={setUseHandScanner}
            setScanCodeString={setScanCodeString}
            handleProductScan={handleProductScan}
            setResults={setResults}
          />
        ) : (
          <div className='barcode-scanner-camera-section'>
            <div id={readerElementId} className='barcode-scanner-reader'>
              {isScanning && <div className='barcode-scanner-line'></div>}
            </div>

            {!isScanning && !error && (
              <div className='barcode-scanner-placeholder'>
                <svg className='barcode-scanner-icon' viewBox='0 0 24 24' fill='none' stroke='currentColor'>
                  <rect x='3' y='3' width='18' height='18' rx='2' ry='2' strokeWidth='2' />
                  <path d='M9 3v18M15 3v18' strokeWidth='2' />
                </svg>
                <p>Camera preview will appear here</p>
              </div>
            )}

            {error && (
              <div className='barcode-scanner-error'>
                <svg className='barcode-scanner-error-icon' viewBox='0 0 24 24' fill='none' stroke='currentColor'>
                  <circle cx='12' cy='12' r='10' strokeWidth='2' />
                  <line x1='12' y1='8' x2='12' y2='12' strokeWidth='2' />
                  <line x1='12' y1='16' x2='12.01' y2='16' strokeWidth='2' />
                </svg>
                <p>{error}</p>
              </div>
            )}

            <div className='barcode-scanner-controls'>
              {!isScanning ? (
                <div className='scanBtnWrapper'>
                  <button onClick={startScanning} className='barcode-scanner-button barcode-scanner-button-primary'>
                    {/* svg camera */}
                    <Camera />
                    Single Scan
                  </button>
                  <button className='barcode-scanner-button barcode-scanner-button-primary'>
                    {/* svg for lighting */}
                    <svg className='barcode-scanner-button-icon' viewBox='0 0 24 24' fill='none' stroke='currentColor'>
                      <path d='M13 2L3 14h9l-1 8L21 10h-9l1-8z' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                    </svg>
                    Batch Scanner
                  </button>
                </div>
              ) : (
                <button onClick={stopScanning} className='barcode-scanner-button barcode-scanner-button-secondary'>
                  <svg className='barcode-scanner-button-icon' viewBox='0 0 24 24' fill='none' stroke='currentColor'>
                    <rect x='6' y='6' width='12' height='12' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                  </svg>
                  Stop Scanning
                </button>
              )}
              <button onClick={() => setUseHandScanner(true)} className='barcode-scanner-button barcode-scanner-button-clear'>
                <Barcode />
                Use Handheld Scanner
              </button>
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className='barcode-scanner-results-section'>
            <div className='barcode-scanner-results-header'>
              <h2 className='barcode-scanner-results-title'>Scan Results ({results.length})</h2>
              <button onClick={clearResults} className='barcode-scanner-clear-button'>
                Clear All
              </button>
            </div>

            <div className='barcode-scanner-results-list'>
              {results.map((result) => (
                <div key={result.id} className='barcode-scanner-result-card'>
                  <div className='barcode-scanner-result-header'>
                    <span className='barcode-scanner-result-format'>{result.format}</span>
                    <span className='barcode-scanner-result-time'>{result.timestamp.toLocaleTimeString()}</span>
                  </div>
                  <div className='barcode-scanner-result-content'>
                    <p className='barcode-scanner-result-text'>{result.text}</p>
                    <button onClick={() => copyToClipboard(result.text, result.id)} className='barcode-scanner-copy-button' title='Copy to clipboard'>
                      {copiedId === result.id ? (
                        <svg viewBox='0 0 24 24' fill='none' stroke='currentColor'>
                          <polyline points='20 6 9 17 4 12' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                        </svg>
                      ) : (
                        <svg viewBox='0 0 24 24' fill='none' stroke='currentColor'>
                          <rect x='9' y='9' width='13' height='13' rx='2' ry='2' strokeWidth='2' />
                          <path d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1' strokeWidth='2' />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {/*  {useSdkScanner && <UseSdkScanner useSdkScanner={useSdkScanner} setUseSdkScanner={setUseSdkScanner} />} */}
    </div>
  );
};

export default BarcodeScanner;

/* const UseSdkScanner = ({ setUseSdkScanner }) => {
  useEffect(() => {
    openScanner();
    return () => {
      setUseSdkScanner(false);
    };
  }, []);

  let licenseKey =
    "QjxYkqmtSUl2NOnQxI8+wHJQ3Mw9bR" +
    "v/XNsIXC/QGpm1lJ0JKNVC6u3or+lg" +
    "LixvA4MXd3inQlwHmA4RHZXAGiOPn7" +
    "9yqhICY0h56A7X6o19wD3mM70+zQbr" +
    "0+j8RwPVkYh5SC6y7lHbGmhPvh5Jn+" +
    "q2WbXeycp/s2IISoLvsC5P12ve93n3" +
    "noat5FnnZbAIW+wOBglsVZtrV4zdA8" +
    "+iPzyOtBT47VQXRrIku7NqeQfNafTX" +
    "xn/TnTNz/VzY96QQuOmgqSAhdvtM5a" +
    "6s3HrN0jxOdg11MpONUMZ+zg5pTiSy" +
    "qAgW+NhBxxcnEEiPx/jWwjEGZRp7IU" +
    "oGFdVdtXnh4A==\nU2NhbmJvdFNESw" +
    "psb2NhbGhvc3R8c3RvcmVsZW5zZS5j" +
    "b20KMTc2NTg0MzE5OQo4Mzg4NjA3Cj" +
    "g=\n";

  const { sdk, ready } = useScanbot({ licenseKey });
  const openScanner = async () => {
    if (!sdk) return;
    // create a default configuration
    const config = new sdk.UI.Config.BarcodeScannerScreenConfiguration();
    // tweak config if you want: config.scannerConfiguration.someField = ...
    // then present RTU UI
    // const result = await sdk.UI.BarcodeScanner.present(config);
    // `result` contains scanned barcode(s) depending on config
    const result = await sdk.UI.createBarcodeScanner(config);
    console.log("scan result", result);
  };

  return <></>;
}; */

const HandheldScanner = ({ setUseHandScanner, setScanCodeString, handleProductScan, setResults }) => {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && inputValue.trim()) {
      const newResult = {
        id: Date.now().toString(),
        text: inputValue.trim(),
        format: "Handheld Scanner",
        timestamp: new Date(),
      };
      setScanCodeString(inputValue.trim());
      handleProductScan(inputValue.trim());
      setResults((prev) => [newResult, ...prev]);

      toast.success(`Scanned: ${inputValue.trim()}`);

      setInputValue("");
    }
  };

  return (
    <div className='barcode-scanner-camera-section'>
      <div style={{ minHeight: "10rem" }} className='barcode-scanner-placeholder'>
        <svg className='barcode-scanner-icon' viewBox='0 0 24 24' fill='none' stroke='currentColor'>
          <rect x='2' y='9' width='20' height='6' rx='1' strokeWidth='2' />
          <path d='M6 9V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4M6 15v4a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-4' strokeWidth='2' />
        </svg>
        <p style={{ textAlign: "center" }}>Click the input below and scan with your handheld device</p>
      </div>

      <div className='barcode-scanner-controls'>
        <input
          ref={inputRef}
          type='text'
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder='Focus here and scan with handheld scanner...'
          className='handHeldInpuScanner'
          autoFocus
        />
        <button onClick={() => setUseHandScanner(false)} className='barcode-scanner-button barcode-scanner-button-primary'>
          <Camera />
          Use Camera Scanner
        </button>
      </div>
    </div>
  );
};

/**/

// BarcodeScanner.jsx
/* import React, { useEffect, useRef, useState } from "react";
import { Html5QrcodeSupportedFormats } from "html5-qrcode"; // kept in case you still use constants elsewhere
import useScanbot from "./useScanbot"; // your hook that initializes Scanbot SDK
import { toast } from "react-hot-toast";
import X from "whatever-icon"; // keep whatever imports you had
import Barcode from "whatever-icon";
import Camera from "whatever-icon";
import PuffLoader from "react-spinners/PuffLoader"; */

/**
 * Replaced Html5Qrcode scanning with Scanbot-based detection.
 * Keeps your UI intact; only camera + detection logic changed.
 */

/* HandheldScanner component remains unchanged from your original file */
